import React, { useMemo } from "react";
import { Button, Modal } from "react-bootstrap";
import McqQuestionPreview from "./McqQuestionPreview";
import CodingQuestionPreview from "./CodingQuestionPreview";
import {
  buildCodingPreview,
  buildMcqPreview,
  isCodingPreviewKind,
  QUESTION_TYPE,
  resolvePreviewKind,
  type PreviewQuestion,
} from "./questionPreviewAdapter";

type QuestionPreviewModalProps = {
  question: PreviewQuestion | null;
  onHide: () => void;
};

const QuestionPreviewModal: React.FC<QuestionPreviewModalProps> = ({
  question,
  onHide,
}) => {
  const kind = useMemo(
    () => (question ? resolvePreviewKind(question) : "unknown"),
    [question]
  );
  const mcq = useMemo(
    () =>
      question && kind === QUESTION_TYPE.MCQ
        ? buildMcqPreview(question)
        : null,
    [question, kind]
  );
  const coding = useMemo(() => {
    if (!question || !isCodingPreviewKind(kind)) return null;
    return buildCodingPreview(question, kind);
  }, [question, kind]);

  return (
    <Modal
      show={Boolean(question)}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="question-trainer-preview-modal"
      className="test-flow-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          Question Preview
          {question?.question_id ? (
            <span className="text-muted fw-normal ms-2">
              ID: {question.question_id}
            </span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {mcq && <McqQuestionPreview model={mcq} />}
        {coding && <CodingQuestionPreview model={coding} />}
        {question && !mcq && !coding && (
          <div className="text-muted small">
            Unable to render this question. Missing options/prompt in question_data.
            <pre className="mt-2 small bg-light border rounded p-2 overflow-auto">
              {JSON.stringify(question.question_data, null, 2)}
            </pre>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionPreviewModal;
